import React, {
  memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import FighterFigure from './stage/FighterFigure';
import {
  Backdrop, Barrel, Floor, PickupShape, Platforms, ProjectileShape, StageDefs, Trap, Vignette,
} from './stage/StageProps';
import {
  ARENA_H, ARENA_W, FIGHTER_H, KIND_SCALE, ST, spellMeta,
} from '../engine/constants';
import '../styles/brawl.css';

/**
 * Pure SVG renderer of a brawl match (PvP Arena / Brawler panels), 2D world (§11.1):
 * x → right, y ↑ up from the floor; drawn at svgY = H − y.
 *
 * Rendering model (cheap re-renders):
 *  - a requestAnimationFrame loop samples `engine.frame(now)` (interpolated + own prediction);
 *  - positions of fighters, shadows, jump trails, projectiles and pickups are written straight
 *    into the DOM via refs;
 *  - React state (`view`) is replaced only when a discrete signature changes;
 *  - snapshot events (`ev`) spawn short-lived FX (damage numbers, sparks, dust, explosions…),
 *    heavy hits shake the camera (not with prefers-reduced-motion), a K.O. flashes the screen
 *    and slows time down for a moment.
 *
 * Props:
 *  engine        createBrawlEngine() instance (or any {frame(now), events(now)})
 *  fighters      match.fighters meta [{idx, team, name, character, spell, maxHp, kind, look}]
 *  you           own fighter idx
 *  world         {w, h, platforms} (see worldOf(match))
 *  looks         {[idx]: look} — fallback looks when a fighter meta has none
 *  theme         'arena' | 'comix'
 *  fit           'contain' — fixed viewBox, the box keeps the world aspect (Brawler panel);
 *                'fill'    — fills the parent; the world is anchored above `reserveBottom` px
 *                            (overlaid controls) and below `reserveTop` px (overlaid HUD), extra
 *                            space shows more sky / arena walls (PvP portrait arena)
 *  slowmoOnKo    K.O. slow-motion (PvP)
 *  onHud         (frame) => void, called when the discrete signature changes
 *  onEvent       (event) => void, for haptics/sounds (called once per server event)
 *  running       false pauses the loop
 */

const HITS = ['POW!', 'WHAM!', 'BAM!', 'SMASH!', 'BIFF!', 'THWACK!'];
const FX_TTL = 950;
const TOP_PAD = 24;
const FLOOR_MIN = 34;
const CONTAIN_FLOOR = 70;
const TRAIL_N = 7;

const prefersReducedMotion = () => {
  try {
    return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    return false;
  }
};

/**
 * viewBox [x, y, w, h] for the world W×H.
 * contain → [0, −TOP_PAD, W, H + TOP_PAD + CONTAIN_FLOOR]
 * fill    → matches the element aspect; scale = min(width / W, worldPx / (H + TOP_PAD + FLOOR_MIN))
 *           with the floor anchored `reserveBottom` px above the bottom edge.
 */
export function computeViewBox({
  w, h, fit = 'contain', box = null, reserveTop = 0, reserveBottom = 0,
}) {
  if (fit !== 'fill' || !box || !(box.width > 0) || !(box.height > 0)) {
    return [0, -TOP_PAD, w, h + TOP_PAD + (fit === 'fill' ? FLOOR_MIN : CONTAIN_FLOOR)];
  }
  const worldPx = Math.max(80, box.height - reserveTop - reserveBottom);
  const need = h + TOP_PAD + FLOOR_MIN;
  const scale = Math.min(box.width / w, worldPx / need);
  const vbW = box.width / scale;
  const vbH = box.height / scale;
  const x0 = (w - vbW) / 2;
  const bottom = h + FLOOR_MIN + reserveBottom / scale;
  return [x0, bottom - vbH, vbW, vbH];
}

function signature(frame) {
  if (!frame) return '';
  const f = frame.f.map((x) => `${x.i}:${x.st}:${x.d}:${x.gr === 0 ? 0 : 1}:${Math.round(x.hp)}:${Math.floor(x.mn || 0)}:${Math.round(x.gd ?? 0)}:${x.cb || 0}:${(x.bf || []).join(',')}:${x.dc ? 1 : 0}`).join('|');
  const p = frame.p.map((x) => `${x.id}${x.k}${x.d}`).join(',');
  const it = frame.it.map((x) => `${x.id}${x.k}`).join(',');
  const br = frame.br.map((x) => `${x.id}${x.alive ? 1 : 0}${x.fuse ? 1 : 0}`).join(',');
  const tr = frame.tr.map((x) => x.id).join(',');
  return `${f}#${p}#${it}#${br}#${tr}#${Math.ceil((frame.tm || 0) / 1000)}`;
}

/** Highest support (platform or floor) at or below (x, y). */
export function supportBelow(platforms, x, y) {
  let best = 0;
  (platforms || []).forEach((p) => {
    if (x >= p.x1 && x <= p.x2 && p.y <= y + 0.5 && p.y > best) best = p.y;
  });
  return best;
}

let fxSeq = 0;

function BrawlStage({
  engine, fighters, you, world, looks, theme = 'arena', fit = 'contain', reserveTop = 0, reserveBottom = 0,
  slowmoOnKo = false, onHud, onEvent, running = true, className = '',
}) {
  const W = Number(world?.w) || ARENA_W;
  const H = Number(world?.h) || ARENA_H;
  const platforms = world?.platforms || [];
  const [view, setView] = useState(() => (engine ? engine.frame(Date.now()) : null));
  const [fx, setFx] = useState([]);
  const [shake, setShake] = useState(null);
  const [box, setBox] = useState(null);
  const wrapRef = useRef(null);
  const sigRef = useRef('');
  const frameRef = useRef(view);
  const posNodes = useRef(new Map());
  const flashNodes = useRef(new Map());
  const refCbs = useRef(new Map());
  const trails = useRef(new Map()); // idx → [[x, y]…]
  const timeRef = useRef({ last: null, lag: 0, slowUntil: 0 });
  const geomRef = useRef({ H, platforms });
  geomRef.current = { H, platforms };
  const onHudRef = useRef(onHud);
  const onEventRef = useRef(onEvent);
  onHudRef.current = onHud;
  onEventRef.current = onEvent;
  const reduced = useMemo(prefersReducedMotion, []);

  const metaByIdx = useMemo(() => {
    const m = new Map();
    (fighters || []).forEach((f) => m.set(f.idx, f));
    return m;
  }, [fighters]);
  const metaRef = useRef(metaByIdx);
  metaRef.current = metaByIdx;

  // ── fill mode: measure the element ─────────────────────────────────────────
  useLayoutEffect(() => {
    if (fit !== 'fill' || !wrapRef.current) return undefined;
    const el = wrapRef.current;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setBox((prev) => (prev && prev.width === r.width && prev.height === r.height ? prev : { width: r.width, height: r.height }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit]);

  const vb = useMemo(() => computeViewBox({
    w: W, h: H, fit, box, reserveTop, reserveBottom,
  }), [W, H, fit, box, reserveTop, reserveBottom]);

  // stable ref callbacks per key (memoised children keep their props)
  const refFor = useCallback((map, key) => {
    const cacheKey = `${map === flashNodes ? 'fl' : 'pos'}:${key}`;
    let cb = refCbs.current.get(cacheKey);
    if (!cb) {
      cb = (node) => {
        if (node) {
          map.current.set(key, node);
          if (map === posNodes) placeNode(node, key, frameRef.current, geomRef.current, trails.current);
        } else {
          map.current.delete(key);
        }
      };
      refCbs.current.set(cacheKey, cb);
    }
    return cb;
  }, []);

  const spawnFx = useCallback((events, frame) => {
    if (!events.length) return;
    const { H: gh } = geomRef.current;
    const pos = (idx) => {
      const f = frame?.f.find((x) => x.i === idx);
      return f ? { x: Number(f.x), sy: gh - (Number(f.y) || 0) } : { x: W / 2, sy: gh };
    };
    const items = [];
    const meta = metaRef.current;
    let shakeLevel = 0;
    let ko = false;
    events.forEach((e) => {
      onEventRef.current?.(e);
      const id = (fxSeq += 1);
      const jitter = ((id * 37) % 30) - 15;
      switch (e.e) {
        case 'hit': {
          const v = pos(e.v);
          const m = meta.get(e.v);
          const hgt = FIGHTER_H * (KIND_SCALE[m?.kind] || 1);
          const node = flashNodes.current.get(e.v);
          if (node) {
            node.classList.remove('bf-flash', 'bf-flash--blk');
            const cls = e.blk ? 'bf-flash--blk' : 'bf-flash';
            // next frame → the CSS animation restarts on consecutive hits
            requestAnimationFrame(() => {
              node.classList.add(cls);
              setTimeout(() => node.classList.remove(cls), 200);
            });
          }
          const dmg = Math.max(0, Math.round(Number(e.dmg) || 0));
          const a = Number.isFinite(e.a) ? pos(e.a) : null;
          const side = a ? (a.x <= v.x ? -1 : 1) : -1; // where the blow came from
          const chestY = v.sy - hgt * 0.62;
          if (e.blk) {
            // chip damage through the block: small grey-blue number + shield spark
            items.push({ id, type: 'dmg', x: v.x + jitter, y: v.sy - hgt - 30, text: `-${dmg}`, cls: 'blk' });
            items.push({ id: `${id}s`, type: 'shield', x: v.x + side * hgt * 0.42, y: chestY, side });
          } else {
            items.push({
              id, type: 'dmg', x: v.x + jitter, y: v.sy - hgt - 34, text: `-${dmg}`, cls: e.crit ? 'crit' : 'norm',
            });
            items.push({ id: `${id}k`, type: 'spark', x: v.x + side * hgt * 0.25, y: chestY + jitter * 0.5, cls: e.crit ? 'crit' : 'norm' });
            items.push({
              id: `${id}w`, type: 'burst', x: v.x + side * 30, y: chestY - 40 + jitter,
              text: e.crit ? 'KRAKOOM!' : HITS[id % HITS.length], cls: e.crit ? 'crit' : 'norm',
            });
            const maxHp = Number(m?.maxHp) || 0;
            const heavy = e.crit || (maxHp > 0 && dmg >= maxHp * 0.08) || e.v === you;
            shakeLevel = Math.max(shakeLevel, e.crit ? 2 : heavy ? 1 : 0);
          }
          break;
        }
        case 'cast': {
          const m = meta.get(e.a);
          const sp = spellMeta(e.k, m?.spell, m?.character);
          const p = pos(e.a);
          items.push({ id, type: 'burst', x: p.x, y: p.sy - FIGHTER_H - 60, text: sp.word || 'ZAP!', cls: 'cast' });
          items.push({ id: `${id}r`, type: 'ring', x: p.x, y: p.sy - FIGHTER_H * 0.55, color: sp.color });
          break;
        }
        case 'pick': {
          const p = pos(e.a);
          const text = e.k === 'HEALTH' ? '+HP' : e.k === 'MANA' ? '+MANA' : 'RAGE!';
          items.push({ id, type: 'word', x: p.x, y: p.sy - FIGHTER_H - 50, text, cls: `pick-${String(e.k).toLowerCase()}` });
          break;
        }
        case 'boom': {
          const bx = Number(e.x);
          const by = Number(e.y) || 0;
          items.push({ id, type: 'boom', x: Number.isFinite(bx) ? bx : W / 2, y: gh - by - 40, text: 'KA-BOOM!' });
          shakeLevel = 2;
          break;
        }
        case 'guard': {
          const p = pos(e.v);
          items.push({ id, type: 'word', x: p.x, y: p.sy - FIGHTER_H - 60, text: 'GUARD BREAK!', cls: 'guard' });
          shakeLevel = Math.max(shakeLevel, 1);
          break;
        }
        case 'ko': {
          const p = pos(e.v);
          items.push({ id, type: 'ko', x: p.x, y: p.sy - FIGHTER_H - 40, text: 'K.O.!' });
          ko = true;
          shakeLevel = 2;
          break;
        }
        case 'jump': {
          const p = pos(e.a);
          items.push({ id, type: 'puff', x: p.x, y: p.sy });
          break;
        }
        case 'land': {
          const p = pos(e.a);
          items.push({ id, type: 'dust', x: p.x, y: p.sy });
          break;
        }
        case 'push': {
          const px = Number(e.x);
          const py = Number(e.y) || 0;
          items.push({ id, type: 'whoosh', x: Number.isFinite(px) ? px : W / 2, y: gh - py - 20, k: e.k });
          break;
        }
        case 'tele': {
          const p = pos(e.a);
          items.push({ id, type: 'word', x: p.x, y: p.sy - FIGHTER_H - 50, text: 'CHARGE!', cls: 'guard' });
          break;
        }
        case 'trap': {
          const p = pos(e.v);
          items.push({ id, type: 'word', x: p.x, y: p.sy - FIGHTER_H - 50, text: 'SLIP!', cls: 'pick-rage' });
          break;
        }
        default:
          break;
      }
    });
    if (ko) {
      items.push({ id: `ko${fxSeq}`, type: 'flash' });
      if (slowmoOnKo) timeRef.current.slowUntil = Date.now() + 700;
    }
    if (shakeLevel > 0 && !reduced) setShake({ n: fxSeq, level: shakeLevel });
    if (!items.length) return;
    const ids = new Set(items.map((i) => i.id));
    setFx((prev) => [...prev.slice(-30), ...items]);
    setTimeout(() => setFx((prev) => prev.filter((i) => !ids.has(i.id))), FX_TTL);
  }, [W, you, reduced, slowmoOnKo]);

  /** Local time, slowed down to 35 % during the K.O. slow-mo, then catching up. */
  const virtualNow = useCallback((now) => {
    const T = timeRef.current;
    if (T.last !== null) {
      const dt = Math.max(0, now - T.last);
      if (now < T.slowUntil) T.lag += dt * 0.65;
      else if (T.lag > 0) T.lag = Math.max(0, T.lag - dt * 0.8);
    }
    T.last = now;
    return now - T.lag;
  }, []);

  const tick = useCallback((now) => {
    if (!engine) return;
    const vnow = virtualNow(now);
    const frame = engine.frame(vnow);
    if (!frame) return;
    frameRef.current = frame;
    posNodes.current.forEach((node, key) => placeNode(node, key, frame, geomRef.current, trails.current));
    const sig = signature(frame);
    if (sig !== sigRef.current) {
      sigRef.current = sig;
      setView(frame);
      onHudRef.current?.(frame);
    }
    if (engine.events) spawnFx(engine.events(vnow), frame);
  }, [engine, spawnFx, virtualNow]);

  useLayoutEffect(() => {
    tick(Date.now());
  }, [tick]);

  useEffect(() => {
    if (!running || !engine) return undefined;
    let raf = null;
    const loop = () => {
      tick(Date.now());
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [running, engine, tick]);

  useEffect(() => {
    if (!shake) return undefined;
    const t = setTimeout(() => setShake(null), 380);
    return () => clearTimeout(t);
  }, [shake]);

  const fightersView = useMemo(() => {
    if (!view) return [];
    return [...view.f].sort((a, b) => (
      (b.st === ST.DEAD) - (a.st === ST.DEAD) || (a.i === you) - (b.i === you)
    ));
  }, [view, you]);

  const ownTeam = metaByIdx.get(you)?.team;
  const pvp = (fighters || []).length <= 2;

  return (
    <div
      ref={wrapRef}
      className={`brawl-stage brawl-stage--${theme} brawl-stage--${fit} ${className}`}
      style={fit === 'fill' ? undefined : { aspectRatio: `${vb[2]} / ${vb[3]}` }}
    >
      <svg
        className="brawl-stage__svg"
        viewBox={vb.map((v) => Math.round(v * 10) / 10).join(' ')}
        preserveAspectRatio="xMidYMax meet"
        role="img"
        aria-label="Fight"
      >
        <StageDefs />
        <g className={`brawl-cam${shake ? ` brawl-cam--shake${shake.level} is-n${shake.n % 2}` : ''}`}>
          <Backdrop theme={theme} w={W} h={H} />
          <Floor theme={theme} w={W} h={H} />
          <Platforms platforms={platforms} h={H} theme={theme} />

          <g className="brawl-layer brawl-layer--barrels">
            {(view?.br || []).map((b) => (
              <Barrel key={b.id} x={Number(b.x)} sy={H - (Number(b.y) || 0)} alive={b.alive} fuse={b.fuse} />
            ))}
          </g>
          <g className="brawl-layer brawl-layer--traps">
            {(view?.tr || []).map((t) => <Trap key={t.id} x={Number(t.x)} sy={H - (Number(t.y) || 0)} />)}
          </g>
          <g className="brawl-layer brawl-layer--shadows">
            {fightersView.map((f) => (
              <ellipse key={f.i} ref={refFor(posNodes, `s${f.i}`)} className="bf-shadow" cx="0" cy="0" rx="30" ry="7" />
            ))}
          </g>
          <g className="brawl-layer brawl-layer--items">
            {(view?.it || []).map((it) => (
              <g key={it.id} ref={refFor(posNodes, `i${it.id}`)}>
                <PickupShape k={it.k} />
              </g>
            ))}
          </g>
          <g className="brawl-layer brawl-layer--trails">
            {fightersView.map((f) => (
              <path key={f.i} ref={refFor(posNodes, `t${f.i}`)} className={`bf-trail${f.i === you ? ' bf-trail--mine' : ''}`} d="" />
            ))}
          </g>

          <g className="brawl-layer brawl-layer--fighters">
            {fightersView.map((f) => {
              const meta = metaByIdx.get(f.i) || { idx: f.i, kind: f.i === you ? 'hero' : 'thug' };
              return (
                <FighterFigure
                  key={f.i}
                  meta={meta}
                  look={meta.look || looks?.[f.i] || null}
                  st={f.st}
                  d={f.d}
                  gr={f.gr === 0 || f.gr === false ? 0 : 1}
                  hp={Math.round(f.hp)}
                  mn={Math.floor(f.mn || 0)}
                  gd={Math.round(f.gd ?? 40)}
                  cb={f.cb}
                  bfKey={(f.bf || []).join(',')}
                  dc={f.dc ? 1 : 0}
                  mine={f.i === you}
                  compactBars={pvp}
                  enemy={ownTeam !== undefined && meta.team !== undefined ? meta.team !== ownTeam : f.i !== you}
                  setPosRef={refFor(posNodes, f.i)}
                  setFlashRef={refFor(flashNodes, f.i)}
                />
              );
            })}
          </g>

          <g className="brawl-layer brawl-layer--projectiles">
            {(view?.p || []).map((p) => {
              const owner = metaByIdx.get(p.o);
              return (
                <g key={p.id} ref={refFor(posNodes, `p${p.id}`)}>
                  <ProjectileShape k={p.k} ownerSpell={owner?.spell || owner?.character} d={p.d} />
                </g>
              );
            })}
          </g>

          <g className="brawl-layer brawl-layer--fx">
            {fx.map((f) => (f.type === 'flash' ? null : <FxItem key={f.id} fx={f} minX={vb[0]} maxX={vb[0] + vb[2]} />))}
          </g>
        </g>
        <Vignette vb={vb} />
        {fx.filter((f) => f.type === 'flash').map((f) => (
          <rect key={f.id} className="bfx-flash" x={vb[0]} y={vb[1]} width={vb[2]} height={vb[3]} fill="#fff" pointerEvents="none" />
        ))}
      </svg>
    </div>
  );
}

/**
 * Writes the transform of a node: fighter (`idx`), shadow (`s<idx>`), jump trail (`t<idx>`),
 * projectile (`p<id>`) or pickup (`i<id>`).
 */
function placeNode(node, key, frame, geom, trails) {
  if (!frame || !node) return;
  const H = geom.H;
  if (typeof key === 'string') {
    const kind = key[0];
    const id = key.slice(1);
    if (kind === 'p') {
      const p = frame.p.find((x) => String(x.id) === id);
      if (p) {
        // projectiles fly at chest height; `y` is their own height (fallback: chest of a floor fighter)
        const py = Number.isFinite(Number(p.y)) && p.y !== undefined ? Number(p.y) : 60;
        node.setAttribute('transform', `translate(${Number(p.x).toFixed(1)} ${(H - py).toFixed(1)})`);
      }
      return;
    }
    if (kind === 'i') {
      const it = frame.it.find((x) => String(x.id) === id);
      if (it) node.setAttribute('transform', `translate(${Number(it.x).toFixed(1)} ${(H - (Number(it.y) || 0) - 20).toFixed(1)})`);
      return;
    }
    const f = frame.f.find((x) => String(x.i) === id);
    if (!f) return;
    const x = Number(f.x);
    const y = Number(f.y) || 0;
    if (kind === 's') {
      const ground = supportBelow(geom.platforms, x, y);
      const k = Math.max(0.35, 1 - (y - ground) / 300);
      node.setAttribute('transform', `translate(${x.toFixed(1)} ${(H - ground + 2).toFixed(1)}) scale(${k.toFixed(2)})`);
      node.setAttribute('opacity', (0.45 * k).toFixed(2));
      return;
    }
    if (kind === 't') {
      const idx = f.i;
      const list = trails.get(idx) || [];
      const air = f.gr === 0 || f.gr === false;
      if (air) list.push([x, H - y - FIGHTER_H * 0.45]);
      else if (list.length) list.shift();
      while (list.length > TRAIL_N) list.shift();
      trails.set(idx, list);
      node.setAttribute('d', list.length > 1 ? `M${list.map((p) => `${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' L')}` : '');
      return;
    }
    return;
  }
  const f = frame.f.find((x) => x.i === key);
  if (f) node.setAttribute('transform', `translate(${Number(f.x).toFixed(1)} ${(H - (Number(f.y) || 0)).toFixed(1)})`);
}

const FxItem = memo(function FxItem({ fx, minX = 0, maxX = 1000 }) {
  const x = Math.max(minX + 50, Math.min(maxX - 50, fx.x));
  switch (fx.type) {
    case 'dmg':
      return (
        <text x={x} y={fx.y} className={`bfx-dmg bfx-dmg--${fx.cls}`} textAnchor="middle">{fx.text}</text>
      );
    case 'burst':
      return (
        <g transform={`translate(${x} ${fx.y})`} className={`bfx-burst bfx-burst--${fx.cls}`}>
          <g className="bfx-burst__pop">
            <path d="M0 -40 L10 -19 L35 -28 L23 -7 L45 3 L21 10 L28 35 L7 19 L-5 40 L-12 17 L-37 28 L-23 5 L-45 -5 L-21 -12 L-30 -35 L-9 -21Z" className="bfx-burst__star" />
            <text y="7" textAnchor="middle" className="bfx-burst__text">{fx.text}</text>
          </g>
        </g>
      );
    case 'spark':
      return (
        <g transform={`translate(${fx.x} ${fx.y})`} className={`bfx-spark bfx-spark--${fx.cls}`}>
          {[0, 45, 90, 135, 180, 225, 270, 315].map((deg, i) => (
            <line key={deg} x1="0" y1="0" x2={i % 2 ? 26 : 40} y2="0" transform={`rotate(${deg + 10})`} className="bfx-spark__ray" />
          ))}
          <circle r="12" className="bfx-spark__core" />
        </g>
      );
    case 'shield':
      return (
        <g transform={`translate(${fx.x} ${fx.y}) scale(${fx.side} 1)`} className="bfx-shield">
          <path d="M-6 -40 Q20 0 -6 40" className="bfx-shield__arc" />
          {[-30, -10, 10, 30].map((dy) => (
            <line key={dy} x1="4" y1={dy} x2="22" y2={dy * 1.3} className="bfx-shield__spark" />
          ))}
        </g>
      );
    case 'ring':
      return <circle cx={fx.x} cy={fx.y} r="40" className="bfx-ring" stroke={fx.color || '#b36bff'} />;
    case 'word':
      return (
        <text x={x} y={fx.y} className={`bfx-word bfx-word--${fx.cls}`} textAnchor="middle">{fx.text}</text>
      );
    case 'boom':
      return (
        <g transform={`translate(${fx.x} ${fx.y})`} className="bfx-boom">
          <circle r="120" fill="url(#brawl-boom)" className="bfx-boom__ball" />
          <text y="-80" textAnchor="middle" className="bfx-boom__text">{fx.text}</text>
        </g>
      );
    case 'ko':
      return (
        <text x={x} y={fx.y} className="bfx-ko" textAnchor="middle">{fx.text}</text>
      );
    case 'dust':
      return (
        <g transform={`translate(${fx.x} ${fx.y})`} className="bfx-dust">
          <ellipse cx="-14" cy="-6" rx="14" ry="7" className="bfx-dust__l" />
          <ellipse cx="14" cy="-6" rx="14" ry="7" className="bfx-dust__r" />
          <ellipse cx="0" cy="-4" rx="20" ry="5" className="bfx-dust__c" />
        </g>
      );
    case 'puff':
      return <ellipse cx={fx.x} cy={fx.y - 3} rx="22" ry="6" className="bfx-puff" />;
    case 'whoosh':
      return (
        <g transform={`translate(${fx.x} ${fx.y})`} className="bfx-whoosh">
          <path d="M-70 -10 H-20 M-90 2 H-24 M-64 14 H-22 M20 -10 H70 M24 2 H90 M22 14 H64" />
          <text y="-30" textAnchor="middle" className="bfx-whoosh__text">WHOOSH!</text>
        </g>
      );
    default:
      return null;
  }
});

export default memo(BrawlStage);
